import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Sparkles, BarChart3, Quote, FileText, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { SearchParam } from '@common/ts/enums';
import {
  useListThreads,
  useGetThread,
  useCreateThread,
  useDeleteThread,
  useGetSuggestions,
  useAnalysisSchemaStatus,
} from '@/reactQuery/researchAssistant';
import { useGetFilters } from '@/reactQuery/get';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import useTranslation from '@/hooks/useTranslation';
import useMediaQuery from '@/hooks/useMediaQuery';
import OutdatedAnalysisDialog from '@/components/Modals/OutdatedAnalysisDialog';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import useResearchChat from './research_assistant/useResearchChat';
import ThreadList from './research_assistant/ThreadList';
import ChatThread from './research_assistant/ChatThread';
import ArtifactPanel from './research_assistant/ArtifactPanel';

// Artifact panel resize constraints. Min width keeps quote cards and
// data tables legible; max width prevents the chat column from becoming
// unusable. Persisted in localStorage so the user's preference survives
// navigation and page reloads.
const ARTIFACT_PANEL_MIN_WIDTH = 320;
const ARTIFACT_PANEL_MAX_WIDTH = 900;
const ARTIFACT_PANEL_DEFAULT_WIDTH = 420;
const ARTIFACT_PANEL_WIDTH_KEY = 'ra:artifactPanelWidth';

function clampArtifactPanelWidth(value: number): number {
  return Math.max(
    ARTIFACT_PANEL_MIN_WIDTH,
    Math.min(ARTIFACT_PANEL_MAX_WIDTH, value),
  );
}

// Height of the fixed project navbar. Used as the baseline offset for
// RA's fixed positioning — the sticky results tab bar height is measured
// dynamically in ProjectResults and passed in via tabBarHeight.
const PROJECT_NAVBAR_HEIGHT = 55;

interface ResearchAssistantProps {
  isRerunLoading?: boolean;
  tabBarHeight?: number;
}

// TODO: fragile dual-default — ProjectResults defaults to 61, keep in sync or extract a shared constant
function ResearchAssistant({ isRerunLoading, tabBarHeight = 61 }: ResearchAssistantProps) {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID) || null;

  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [showArtifacts, setShowArtifacts] = useState(false);
  const [deletingThreadId, setDeletingThreadId] = useState<string | null>(null);

  // Below the lg breakpoint the artifact panel is rendered in a right-side
  // Sheet slide-over instead of inline. isLg gates the inline layout branch.
  const isLg = useMediaQuery('(min-width: 1024px)');
  const [artifactSheetOpen, setArtifactSheetOpen] = useState(false);

  // When the viewport crosses above lg, close the narrow-width artifact
  // Sheet so it doesn't re-open unexpectedly if the user resizes back
  // below lg later.
  useEffect(() => {
    if (isLg) setArtifactSheetOpen(false);
  }, [isLg]);

  // Artifact panel width — initial value pulled from localStorage (safely,
  // so SSR/tests with no window still get the default). Changes are
  // persisted on drag end via the handle below.
  const [artifactPanelWidth, setArtifactPanelWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return ARTIFACT_PANEL_DEFAULT_WIDTH;
    const stored = window.localStorage.getItem(ARTIFACT_PANEL_WIDTH_KEY);
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return Number.isFinite(parsed)
      ? clampArtifactPanelWidth(parsed)
      : ARTIFACT_PANEL_DEFAULT_WIDTH;
  });
  const artifactResizeStateRef = useRef<{
    startX: number;
    startWidth: number;
  } | null>(null);

  // Mouse-drag resize for the artifact panel. The handle lives on the
  // panel's left edge; dragging left expands it, dragging right shrinks
  // it. We subtract from startWidth so the panel follows the cursor.
  const handleArtifactResizeStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      artifactResizeStateRef.current = {
        startX: e.clientX,
        startWidth: artifactPanelWidth,
      };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const handleMouseMove = (ev: MouseEvent) => {
        const state = artifactResizeStateRef.current;
        if (!state) return;
        const delta = state.startX - ev.clientX;
        const nextWidth = clampArtifactPanelWidth(state.startWidth + delta);
        setArtifactPanelWidth(nextWidth);
      };
      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        // Persist the final width so it survives reloads. Use the
        // functional setter to read the up-to-date width from React
        // state (the closure captured the old value at mousedown time).
        try {
          setArtifactPanelWidth((current) => {
            window.localStorage.setItem(ARTIFACT_PANEL_WIDTH_KEY, String(current));
            return current;
          });
        } catch {
          // Ignore localStorage failures (quota exceeded, disabled, etc.)
        }
        artifactResizeStateRef.current = null;
      };
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [artifactPanelWidth],
  );

  // Track which thread we last loaded into local state. This avoids re-running
  // the load effect on every query invalidation (which happens after sendMessage
  // and would otherwise overwrite streaming state).
  const loadedThreadIdRef = useRef<string | null>(null);
  // Track previous artifacts.length so the auto-open effect only fires when
  // a NEW artifact arrives — not every time `showArtifacts` flips to false.
  // Without this guard, the close button would be useless because the effect
  // would re-open the panel on the next render.
  const prevArtifactCountRef = useRef(0);

  // ── Data fetching ─────────────────────────────────────────────────

  const { currentProject } = useCurrentProject();
  const { data: threads = [], isLoading: threadsLoading } = useListThreads(projectId, filterId);
  const { data: activeThread, isLoading: activeThreadLoading } = useGetThread(
    projectId,
    activeThreadId,
  );
  const { data: suggestions = [] } = useGetSuggestions(projectId, filterId);
  const { data: projectFilters = [] } = useGetFilters(projectId);
  const createThread = useCreateThread(projectId);
  const deleteThread = useDeleteThread(projectId);

  // B1.f — Pre-flight check: does this (project, filter) slot have the
  // rich analysis schema the Research Assistant needs? If not, render
  // the OutdatedAnalysisDialog so the user can re-run analysis with
  // force=true. The check is filter-aware because each filter has its
  // own overall_pages document, and a project can have a current
  // no-filter analysis but stale filter-specific ones.
  const { data: schemaStatus, refetch: refetchSchemaStatus } = useAnalysisSchemaStatus(
    projectId,
    filterId,
  );
  const [schemaDialogDismissed, setSchemaDialogDismissed] = useState(false);
  // Reset the dismissed state when filter changes — switching to a
  // different filter is effectively re-entering the gate.
  useEffect(() => {
    setSchemaDialogDismissed(false);
  }, [filterId]);
  const showSchemaDialog =
    !!schemaStatus && !schemaStatus.current && !schemaDialogDismissed && !isRerunLoading;

  // Resolve filter name from filter_id. The FiltersDataToSendProps type
  // in the global declaration is incomplete (missing _id), so we cast
  // through unknown to a narrow inline type here.
  const filterName = useMemo(() => {
    if (!filterId) return null;
    const filters = projectFilters as unknown as Array<{
      _id: string;
      name: string;
    }>;
    const f = filters.find((x) => x._id === filterId);
    return f?.name || null;
  }, [filterId, projectFilters]);

  // Get respondent count from project metadata
  const respondentCount = currentProject?.respondents_count;
  const interviewGoal = currentProject?.interview_goal;

  // ── Chat hook ─────────────────────────────────────────────────────

  const {
    messages,
    artifacts,
    isStreaming,
    error,
    sendMessage,
    stopStreaming,
    retry,
    setMessages,
    setArtifacts,
    activeToolSteps,
  } = useResearchChat(projectId, activeThreadId, filterId, () => {
    if (document.hidden || !document.hasFocus()) {
      toast.success(lang.get('msg.raResultsReady'));
    }
  });

  // Local error state copy so we can dismiss it. The chat hook owns the
  // primary error state but we shadow it here to allow the user to clear it.
  const [dismissedError, setDismissedError] = useState<string | null>(null);
  const visibleError = error && error !== dismissedError ? error : null;
  const handleDismissError = useCallback(() => {
    if (error) setDismissedError(error);
  }, [error]);
  const handleRetry = useCallback(() => {
    setDismissedError(null);
    retry();
  }, [retry]);

  // ── Reset thread selection when filter changes ────────────────────
  // When the user switches the dashboard filter, the threads list refetches
  // for the new filter scope. We must clear activeThreadId so the auto-select
  // effect picks the most recent thread for the new filter — otherwise the
  // user would still see a thread from the old filter scope.
  const prevFilterIdRef = useRef(filterId);
  useEffect(() => {
    if (prevFilterIdRef.current !== filterId) {
      stopStreaming();
      setActiveThreadId(null);
      setMessages([]);
      setArtifacts([]);
      setShowArtifacts(false);
      loadedThreadIdRef.current = null;
      prevFilterIdRef.current = filterId;
    }
  }, [filterId, stopStreaming, setMessages, setArtifacts]);

  // ── Auto-select most recent thread ────────────────────────────────

  useEffect(() => {
    if (!activeThreadId && threads.length > 0) {
      setActiveThreadId(threads[0]._id);
    }
  }, [threads, activeThreadId]);

  // ── Load thread messages when thread changes ──────────────────────
  // Only loads on actual thread switch, not on every query invalidation.
  // This preserves streaming state during/after sendMessage.
  useEffect(() => {
    if (!activeThread) return;
    // String comparison handles both ObjectId-stringified _id and plain string.
    const activeId = String(activeThread._id);
    if (loadedThreadIdRef.current === activeId) return;

    const chatMessages = activeThread.messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m, idx) => {
        // Extract text from string OR array content blocks
        const text =
          typeof m.content === 'string'
            ? m.content
            : Array.isArray(m.content)
              ? m.content
                  .filter((b: any) => b.type === 'text')
                  .map((b: any) => b.text)
                  .join('')
              : '';
        return {
          id: `stored-${activeId}-${idx}`,
          role: m.role as 'user' | 'assistant',
          content: text,
          created_at: m.created_at,
        };
      })
      // Drop assistant messages that have NO text content (i.e., tool_use-only
      // intermediate messages from a multi-step turn). These would render as
      // empty chat bubbles. Their tool steps are not reconstructed for loaded
      // threads, but the final assistant response (which contains the actual
      // answer) IS preserved because it has text content.
      .filter((m) => m.role !== 'assistant' || m.content.length > 0);
    setMessages(chatMessages);
    setArtifacts(activeThread.artifacts || []);
    loadedThreadIdRef.current = activeId;
  }, [activeThread, setMessages, setArtifacts]);

  // ── Auto-open artifact panel when a NEW artifact arrives ─────────
  // Critical: only open when artifacts.length GREW since last render. If
  // we open whenever artifacts.length > 0 && !showArtifacts, the close
  // button would be useless because the effect would re-open immediately.
  // At narrow widths, the same growth condition auto-opens the Sheet —
  // if the user deliberately closes it during streaming, the NEXT
  // artifact still re-opens because each new artifact grows the count.
  useEffect(() => {
    if (artifacts.length > prevArtifactCountRef.current) {
      setShowArtifacts(true);
      setArtifactSheetOpen(true);
    }
    prevArtifactCountRef.current = artifacts.length;
  }, [artifacts.length]);

  // ── Handlers ──────────────────────────────────────────────────────

  const handleSelectThread = useCallback(
    (threadId: string) => {
      // Abort any in-flight stream BEFORE switching threads — otherwise the
      // streaming deltas would continue firing setMessages() into the new
      // thread's view, corrupting state.
      stopStreaming();
      setActiveThreadId(threadId);
      setShowArtifacts(false);
      loadedThreadIdRef.current = null;
    },
    [stopStreaming],
  );

  const handleNewThread = useCallback(async () => {
    stopStreaming();
    const result = await createThread.mutateAsync(filterId);
    setActiveThreadId(result._id);
    setMessages([]);
    setArtifacts([]);
    setShowArtifacts(false);
    loadedThreadIdRef.current = result._id;
  }, [createThread, filterId, setMessages, setArtifacts, stopStreaming]);

  const handleDeleteThread = useCallback(
    async (threadId: string) => {
      // Abort streaming if we're deleting the active thread
      if (activeThreadId === threadId) {
        stopStreaming();
      }
      setDeletingThreadId(threadId);
      try {
        await deleteThread.mutateAsync(threadId);
        if (activeThreadId === threadId) {
          // Pick the next available thread if any (excluding the just-deleted one)
          const nextThread = threads.find((t) => t._id !== threadId);
          setActiveThreadId(nextThread?._id ?? null);
          setMessages([]);
          setArtifacts([]);
          setShowArtifacts(false);
          loadedThreadIdRef.current = null;
        }
      } finally {
        setDeletingThreadId(null);
      }
    },
    [deleteThread, activeThreadId, setMessages, setArtifacts, stopStreaming, threads],
  );

  const handleSendMessage = useCallback(
    async (message: string) => {
      try {
        // Auto-create thread if none selected. Pass the new threadId directly
        // to sendMessage so we don't depend on React state propagation.
        if (!activeThreadId) {
          const result = await createThread.mutateAsync(filterId);
          setActiveThreadId(result._id);
          loadedThreadIdRef.current = result._id;
          await sendMessage(message, result._id);
          return;
        }
        await sendMessage(message);
      } catch (err: any) {
        // Errors from createThread or sendMessage would otherwise be
        // swallowed. The chat hook handles its own errors via setError,
        // but createThread mutations need explicit handling.
        // eslint-disable-next-line no-console
        console.error('Failed to send message:', err);
      }
    },
    [activeThreadId, createThread, filterId, sendMessage],
  );

  // ── Layout ────────────────────────────────────────────────────────
  // Full-bleed: no rounded card, no shadow, no outer border.
  //
  // We use `fixed` positioning pinned to `top` = navbar + tab bar so the
  // Research Assistant sits exactly below the fixed header + sub-tabs
  // chrome and fills the rest of the viewport down to the bottom edge.
  // This bypasses the flex chain of main → TabsContent entirely:
  //   - no flex-height math to chase
  //   - no dependency on parent `h-full` resolving correctly
  //   - no interaction with the `p-[2%]` padding on <main> because
  //     fixed elements are positioned relative to the viewport, not
  //     the padded parent box
  //   - no outer-page scroll because the element is out of flow
  //
  // The top offset is computed dynamically from the project navbar
  // constant plus the measured tab bar height passed in from
  // ProjectResults. This handles tab bar wrapping at narrow widths
  // without hardcoding a fallback.
  //
  // No border-t: the sub-tabs bar already has border-b; stacking
  // another border would produce a double divider line.
  return (
    <div
      style={{ top: PROJECT_NAVBAR_HEIGHT + tabBarHeight }}
      className="fixed left-0 right-0 bottom-0 flex overflow-hidden bg-white"
    >
      {/* Thread list sidebar — Sprint A audit fix (B-2): lowered the
          visibility breakpoint from lg (1024px) to md (768px) so users
          on common laptop resolutions (1280px logical at 125% browser
          zoom = ~1024px effective) still see the thread switcher.
          Below md the sidebar is hidden — sub-tablet screens are out
          of scope for the dashboard chrome anyway. */}
      <div className="w-[240px] shrink-0 hidden md:block">
        <ThreadList
          threads={threads}
          activeThreadId={activeThreadId}
          isLoading={threadsLoading}
          onSelectThread={handleSelectThread}
          onNewThread={handleNewThread}
          onDeleteThread={handleDeleteThread}
          isCreating={createThread.isPending}
          deletingThreadId={deletingThreadId}
          analyzedAt={schemaStatus?.analyzed_at}
        />
      </div>

      {/* Chat thread — flexible center */}
      <div className="flex-1 min-w-0">
        <ChatThread
          messages={messages}
          isStreaming={isStreaming}
          activeToolSteps={activeToolSteps}
          error={visibleError}
          onSendMessage={handleSendMessage}
          onStopStreaming={stopStreaming}
          onDismissError={handleDismissError}
          onRetry={handleRetry}
          projectTitle={currentProject?.title}
          filterName={filterName}
          interviewGoal={interviewGoal}
          respondentCount={respondentCount}
          suggestions={suggestions}
          isLoadingThread={activeThreadLoading && !messages.length}
          isCreatingThread={createThread.isPending}
          isRerunLoading={isRerunLoading}
          allArtifacts={artifacts}
          headerRight={
            // Below lg, show a trigger button for the artifact Sheet.
            // While streaming with no artifacts yet, render a spinner so
            // the user knows an artifact is being generated (the chat
            // often says "see the artifact panel →" before the artifact
            // actually arrives). Once artifacts exist, render the
            // Sparkles icon with a count badge.
            !isLg && (isStreaming || artifacts.length > 0) ? (
              <button
                type="button"
                onClick={() => setArtifactSheetOpen(true)}
                aria-label={
                  artifacts.length > 0
                    ? `${lang.get('msg.raOpenArtifacts')} (${artifacts.length})`
                    : lang.get('msg.raGeneratingArtifact')
                }
                className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-primary/10 border border-primary/20 text-primary text-xs font-semibold hover:bg-primary/15 transition-colors"
              >
                {artifacts.length === 0 ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" />
                )}
                <span>{artifacts.length > 0 ? artifacts.length : '…'}</span>
              </button>
            ) : null
          }
        />
      </div>

      {/* Artifact panel — right side, shown when artifacts exist.
          Resizable via the drag handle on its left edge; width is
          clamped to ARTIFACT_PANEL_MIN/MAX_WIDTH and persisted to
          localStorage so the user's preference survives navigation.
          Sprint A audit fix (B-2/H-6): hidden below lg (1024px) so
          standard laptops still see the panel. */}
      {showArtifacts && artifacts.length > 0 && (
        <div
          className="relative shrink-0 hidden lg:block"
          style={{ width: `${artifactPanelWidth}px` }}
        >
          {/* Drag handle — a thin 4px strip centred on the panel's
              left border. Hover widens the hit area slightly and
              adds a visible indicator so keyboard/screen-reader
              users know it's interactive. Positioned with -left-1
              so the handle straddles the border. */}
          <div
            role="separator"
            aria-label="Resize artifact panel"
            aria-orientation="vertical"
            aria-valuenow={artifactPanelWidth}
            aria-valuemin={ARTIFACT_PANEL_MIN_WIDTH}
            aria-valuemax={ARTIFACT_PANEL_MAX_WIDTH}
            tabIndex={0}
            onMouseDown={handleArtifactResizeStart}
            onKeyDown={(e) => {
              // Left/right arrow keys resize by 16px increments for
              // keyboard users.
              if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setArtifactPanelWidth((w) => {
                  const next = clampArtifactPanelWidth(w + 16);
                  try {
                    window.localStorage.setItem(
                      ARTIFACT_PANEL_WIDTH_KEY,
                      String(next),
                    );
                  } catch {
                    /* ignore */
                  }
                  return next;
                });
              } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                setArtifactPanelWidth((w) => {
                  const next = clampArtifactPanelWidth(w - 16);
                  try {
                    window.localStorage.setItem(
                      ARTIFACT_PANEL_WIDTH_KEY,
                      String(next),
                    );
                  } catch {
                    /* ignore */
                  }
                  return next;
                });
              }
            }}
            className="absolute -left-1 top-0 bottom-0 w-2 cursor-col-resize z-10 group focus:outline-none"
          >
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-neutral-200 group-hover:bg-primary group-hover:w-0.5 group-focus-visible:bg-primary group-focus-visible:w-0.5 transition-colors" />
          </div>
          <ArtifactPanel artifacts={artifacts} onClose={() => setShowArtifacts(false)} />
        </div>
      )}

      {/* Reopen button when artifacts exist but panel is closed */}
      {!showArtifacts && artifacts.length > 0 && (
        <button
          type="button"
          onClick={() => setShowArtifacts(true)}
          aria-label={`${lang.get('msg.raOpenArtifacts')} (${artifacts.length})`}
          className="hidden lg:flex flex-col items-center justify-center gap-2 px-3 py-4 border-l border-neutral-200 bg-gradient-to-b from-primary/5 to-light-pink/30 hover:from-primary/10 hover:to-light-pink/40 transition-colors group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        >
          <Sparkles className="h-4 w-4 text-primary group-hover:scale-110 transition-transform" />
          <span className="text-[10px] font-bold text-primary [writing-mode:vertical-rl] rotate-180 tracking-wider">
            {artifacts.length} {lang.get('msg.raArtifacts').toUpperCase()}
          </span>
        </button>
      )}

      {/* Artifact Sheet — below lg only. Uses modal={false} so the chat
          stays interactive while the sheet is open (researcher can ask
          follow-up questions while viewing artifacts). Explicit z-[100]
          ensures the sheet renders ABOVE the sticky z-[99] results
          tab bar. */}
      {!isLg && (
        <Sheet
          open={artifactSheetOpen}
          onOpenChange={setArtifactSheetOpen}
          modal={false}
        >
          <SheetContent
            side="right"
            className="w-full sm:w-[420px] sm:min-w-[380px] max-w-[560px] p-0 flex flex-col z-[100] [&>button:first-child]:hidden"
          >
            <SheetTitle className="sr-only">
              {lang.get('msg.raArtifacts')}
            </SheetTitle>
            {artifacts.length > 0 ? (
              <ArtifactPanel
                artifacts={artifacts}
                onClose={() => setArtifactSheetOpen(false)}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <Loader2 className="h-6 w-6 text-primary animate-spin mb-3" />
                <p className="text-sm font-medium text-neutral-700">
                  {lang.get('msg.generatingInsights')}
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  {lang.get('msg.generatingInsightsDesc')}
                </p>
              </div>
            )}
          </SheetContent>
        </Sheet>
      )}

      {/* B1.f — Outdated-analysis gate. Rendered alongside the main
          layout (not instead of it) so the underlying chrome stays
          visible behind the modal backdrop while the user decides. */}
      {showSchemaDialog && (
        <OutdatedAnalysisDialog
          isOpen={showSchemaDialog}
          reason={schemaStatus.reason || 'old_schema'}
          feature="research_assistant"
          projectId={projectId}
          filterId={filterId}
          onCancel={() => setSchemaDialogDismissed(true)}
          onReRunComplete={() => {
            setSchemaDialogDismissed(true);
            refetchSchemaStatus();
          }}
        />
      )}

      {/* Branded placeholder when no artifacts yet — fills the right side
          so the layout doesn't feel empty. When streaming, show a loading
          indicator so the user knows an artifact is on its way (the chat
          often says "see the artifact panel →" before the artifact arrives). */}
      {!showArtifacts && artifacts.length === 0 && (
        <div className="w-[280px] shrink-0 hidden lg:flex flex-col border-l border-neutral-200 bg-gradient-to-b from-neutral-50/50 to-primary/5 p-6">
          <div className="flex items-center justify-center h-12 w-12 rounded-2xl bg-white border border-neutral-200 shadow-sm mb-4 mx-auto">
            {isStreaming ? (
              <Loader2 className="h-5 w-5 text-primary animate-spin" />
            ) : (
              <Sparkles className="h-5 w-5 text-primary" />
            )}
          </div>
          <h3 className="text-sm font-semibold text-neutral-700 text-center mb-1">
            {isStreaming ? lang.get('msg.generatingInsights') : lang.get('msg.insightsAppearHere')}
          </h3>
          <p className="text-xs text-neutral-500 text-center mb-6 leading-relaxed">
            {isStreaming
              ? lang.get('msg.generatingInsightsDesc')
              : lang.get('msg.insightsAppearHereDesc')}
          </p>
          <div className="flex flex-col gap-2">
            {[
              {
                icon: Quote,
                label: lang.get('msg.quoteCollections'),
                color: 'text-chat-primary bg-light-pink border-chat-primary/30',
              },
              {
                icon: BarChart3,
                label: lang.get('msg.dataTablesCharts'),
                color: 'text-secondary bg-secondary/15 border-secondary/30',
              },
              {
                icon: FileText,
                label: lang.get('msg.markdownReports'),
                color: 'text-primary bg-primary/10 border-primary/20',
              },
            ].map((item) => (
              <div
                key={item.label}
                className={`flex items-center gap-2 text-[11px] font-medium px-2.5 py-1.5 rounded-lg border ${item.color}`}
              >
                <item.icon className="h-3 w-3" />
                {item.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ResearchAssistant;
